<script lang="ts">
  import type { Rotation } from '$lib/model/types';
  import { projectStore } from '$lib/state/project.svelte';
  import { triggerRecompute } from '$lib/compute/recompute.svelte';

  const inputStep = $derived(
    projectStore.pipeline.steps[projectStore.pipeline.root_id],
  );

  const rotation = $derived(
    (inputStep?.params as { rotation: Rotation } | undefined)?.rotation ?? 0,
  );

  const rotations: Rotation[] = [0, 90, 180, 270];
  const labels: Record<Rotation, string> = {
    0: '0°',
    90: '↻ 90°',
    180: '⥁ 180°',
    270: '↺ 270°',
  };

  function setRotation(r: Rotation) {
    if (!inputStep) return;
    (inputStep.params as { rotation: Rotation }).rotation = r;
    triggerRecompute();
  }
</script>

{#if projectStore.input_asset && inputStep}
  <section class="panel">
    <header>
      <h3>Input</h3>
      <span class="meta">{projectStore.input_asset.filename}</span>
    </header>
    <dl class="meta-list">
      <dt>Resolution</dt>
      <dd>
        {projectStore.input_asset.source_width} ×
        {projectStore.input_asset.source_height} px
      </dd>
      <dt>Source DPI</dt>
      <dd>{projectStore.input_asset.source_dpi}</dd>
      <dt>Format</dt>
      <dd>{projectStore.input_asset.mime}</dd>
    </dl>
    <div class="rotation">
      <div class="field-label" id="rotation-label">Rotation</div>
      <div class="row" role="group" aria-labelledby="rotation-label">
        {#each rotations as r (r)}
          <button
            class="rot"
            class:active={rotation === r}
            onclick={() => setRotation(r)}
            aria-pressed={rotation === r}
          >
            {labels[r]}
          </button>
        {/each}
      </div>
    </div>
  </section>
{/if}

<style>
  .panel {
    display: flex;
    flex-direction: column;
    gap: var(--gap-m);
  }
  header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--gap-m);
  }
  h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-dim);
  }
  .meta {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-dim);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
  }
  .meta-list {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 4px var(--gap-m);
    margin: 0;
    font-size: 12px;
  }
  .meta-list dt {
    color: var(--text-dim);
  }
  .meta-list dd {
    margin: 0;
    font-family: var(--font-mono);
  }
  .field-label {
    color: var(--text-dim);
    font-size: 12px;
    margin-bottom: 4px;
  }
  .row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
  }
  .rot {
    font-family: var(--font-mono);
    font-size: 12px;
    padding: 6px 4px;
  }
  .rot.active {
    background: var(--accent-dim);
    border-color: var(--accent);
    color: var(--bg);
  }
</style>
